import React from 'react';
import isEqual from 'react-fast-compare';
import toString from '../utils/toString';
import getFormattedValue from '../utils/getFormattedValue';
import getCaretPosition from '../utils/getCaretPosition';
import getNumberMask from '../utils/getNumberMask';

const parseMask = (mask, { maskParams = [] }) => {
  if (mask === 'NUMBER_MASK') {
    if (maskParams && !Array.isArray(maskParams)) {
      throw new Error('maskParams should be an array of parameters to pass to the mask function');
    }

    return getNumberMask(...maskParams);
  }

  if (typeof mask === 'string') {
    return mask.split('').map((char) => {
      if (char === '#') {
        return /\d/;
      }

      return char;
    });
  }

  return mask;
};

const parseValue = (value, mask, props) => {
  const { placeholderChar } = props;

  if (typeof mask === 'function' && typeof mask.parseValue === 'function') {
    return mask.parseValue(value, props);
  }

  if (value == null) {
    return '';
  }

  const strValue = toString(value);
  const placeholderPos = strValue.indexOf(placeholderChar);

  if (placeholderPos !== -1) {
    return strValue.substring(0, placeholderPos);
  }

  return strValue;
};

const unparseValue = (value, mask, props) => {
  if (typeof mask === 'function' && typeof mask.unparseValue === 'function') {
    return mask.unparseValue(value, props);
  }

  if (value == null) {
    return '';
  }

  return toString(value);
};

export default function withFormatted(InputComponent) {
  const displayName = InputComponent.displayName || InputComponent.name || 'Component';

  return class FormInputFormatted extends React.Component {
    inputRef = React.createRef();

    static displayName = `withFormatted(${displayName})`;

    static defaultProps = {
      guide: false,
      placeholderChar: '_',
      showMask: false,
    };

    constructor(...args) {
      super(...args);

      const { mask, value } = this.props;
      const parsedMask = parseMask(mask, this.props);
      const parsedValue = parseValue(value, parsedMask, this.props);

      this.state = {
        formattedValue: '',
        isProcessedOnce: false,
        lastMask: parsedMask,
        lastValue: parsedValue,
        placeholder: '',
      };
    }

    componentDidUpdate() {
      if (this.nextSelection) {
        const { start, end } = this.nextSelection;

        this.setSelectionRange(start, end);

        delete this.nextSelection;
      }
    }

    onChange = (e) => {
      e.persist();

      const { guide, mask, onChange, placeholderChar } = this.props;
      const {
        lastValue,
        formattedValue: lastFormattedValue,
        placeholder: lastPlaceholder,
      } = this.state;
      const parsedMask = parseMask(mask, this.props);
      const {
        target: { value, selectionEnd: currCaretPos },
      } = e;
      const { caretTrapIndexes, placeholder, value: formattedValue } = getFormattedValue({
        guide,
        lastFormattedValue,
        mask: parsedMask,
        placeholderChar,
        startCaretPos: this.startCaretPos,
        value,
      });
      const caretPos = getCaretPosition({
        caretTrapIndexes,
        currCaretPos,
        formattedValue,
        lastFormattedValue,
        lastPlaceholder,
        placeholder,
        value,
      });
      const parsedValue = parseValue(formattedValue, parsedMask, this.props);

      // commit new value
      e.target.value = parsedValue;

      this.nextSelection = { start: caretPos, end: caretPos };

      this.setState({
        formattedValue,
        placeholder,
        lastValue: parsedValue,
      });

      if (parsedValue !== lastValue && onChange) {
        onChange(e);
      }
    };

    onKeyDown = (e) => {
      const {
        target: { selectionEnd: currCaretPos },
      } = e;

      this.startCaretPos = currCaretPos;
    };

    static getDerivedStateFromProps(props, state) {
      const { guide, mask, placeholderChar, value } = props;

      const { isProcessedOnce, lastMask, lastValue } = state;
      const parsedMask = parseMask(mask, props);
      const unparsedValue = unparseValue(value, parsedMask, props);

      let { formattedValue, placeholder } = state;
      let parsedValue = lastValue;

      const { placeholder: nextPlaceholder, value: nextFormattedValue } = getFormattedValue({
        guide,
        lastFormattedValue: formattedValue,
        mask: parsedMask,
        placeholderChar,
        skipDeletion: true,
        value: unparsedValue,
      });
      const nextParsedValue = parseValue(nextFormattedValue, parsedMask, props);

      if (isProcessedOnce) {
        if (nextParsedValue !== parsedValue || !isEqual(parsedMask, lastMask)) {
          formattedValue = nextFormattedValue;
          parsedValue = nextParsedValue;
          placeholder = nextPlaceholder;
        }
      } else {
        if (value != null && value !== '') {
          formattedValue = nextFormattedValue;
          parsedValue = nextParsedValue;
        }
        placeholder = nextPlaceholder;
      }

      return {
        formattedValue,
        isProcessedOnce: true,
        lastMask: parsedMask,
        lastValue: parsedValue,
        placeholder,
      };
    }

    setSelectionRange(start, end) {
      const inputRef = this.inputRef.current;

      inputRef.setSelectionRange(start, end);
    }

    render() {
      const {
        guide,
        mask,
        onChange,
        placeholder,
        placeholderChar,
        showMask,
        value,
        ...props
      } = this.props;

      const { formattedValue, placeholder: statePlaceholder } = this.state;

      if (showMask) {
        props.placeholder = statePlaceholder;
      } else {
        props.placeholder = placeholder;
      }

      return (
        <InputComponent
          type="text"
          {...props}
          ref={this.inputRef}
          value={formattedValue}
          onKeyDown={this.onKeyDown}
          onChange={this.onChange}
        />
      );
    }
  };
}
